// Project files: the client-side upload checks (the API checks again by
// content) and the labels the files panel shows.
import type { GlyphName } from "../ui/icons";

/** Per file, as the API enforces it (multipart field "files"). */
export const PROJECT_FILE_MAX_BYTES = 10 * 1024 * 1024;
/** Files per upload batch. */
export const PROJECT_FILE_BATCH = 5;
/** The file picker's filter; the extension decides on the client. */
export const PROJECT_FILE_ACCEPT = ".png,.jpg,.jpeg,.webp,.pdf,.docx,.xlsx,.txt";

const ALLOWED_EXT = new Set(["png", "jpg", "jpeg", "webp", "pdf", "docx", "xlsx", "txt"]);

export type ProjectFileKind = "upload" | "generated" | "drive";

export const KIND_LABEL: Record<ProjectFileKind, string> = {
  upload: "Diunggah",
  generated: "Dibuat Pluto",
  drive: "Drive",
};

/** One calm line when the files cannot be stored: they live in Google Drive, which is not set up here. */
export const DRIVE_OFF_TEXT = "File project disimpan di Google Drive, yang belum disiapkan untuk akun ini.";
export const DRIVE_ONLY_HINT = "Tersimpan di Google Drive";

function extOf(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot < 0 ? "" : name.slice(dot + 1).toLowerCase();
}

/** The first problem with a batch, named for the person, or null when it may be sent. */
export function checkProjectFiles(files: File[]): string | null {
  if (files.length === 0) return null;
  if (files.length > PROJECT_FILE_BATCH) return `Pilih paling banyak ${PROJECT_FILE_BATCH} file sekali unggah.`;
  for (const f of files) {
    if (!ALLOWED_EXT.has(extOf(f.name))) return `${f.name} belum didukung. Pilih PNG, JPG, WEBP, PDF, DOCX, XLSX, atau TXT.`;
    if (f.size > PROJECT_FILE_MAX_BYTES) return `${f.name} berukuran ${formatFileSize(f.size)}, melebihi batas 10 MB.`;
    if (f.size === 0) return `${f.name} kosong.`;
  }
  return null;
}

const sizeFmt = new Intl.NumberFormat("id-ID", { maximumFractionDigits: 1 });

/** 812 B, 1,8 KB, 2 MB (Indonesian decimal comma). */
export function formatFileSize(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes) || bytes < 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${sizeFmt.format(bytes / 1024)} KB`;
  return `${sizeFmt.format(bytes / (1024 * 1024))} MB`;
}

/** The row's type glyph: images get the gallery, everything else a document. */
export function fileGlyph(mimeType: string, name: string): GlyphName {
  const ext = extOf(name);
  if (mimeType.startsWith("image/") || ext === "png" || ext === "jpg" || ext === "jpeg" || ext === "webp") return "gallery";
  return "documentText";
}
